import type {
  ColumnDef,
  ColumnFilter,
  SortingState,
} from "@tanstack/react-table";
import { type PropsWithChildren, useMemo, useState } from "react";

import { TableInstanceProvider } from "@/components/shared/data-table";
import useTableFilters from "@/components/shared/tables/filterPersistance";
import useGlobalFilters from "@/components/shared/tables/globalFilterPersistence";
import {
  type ExpandedState,
  getExpandedRowModel,
} from "@/lib/table/row-expanding";

type Props = PropsWithChildren<{
  tableName: string;
  tableData: any[];
  columns: ColumnDef<any>[];
  initialColumnFilters?: ColumnFilter[];
  initialColumnVisibility?: Record<string, boolean>;
  initialSorting?: SortingState;
  clickableColumns?: string[];
  keepExpandedRowsOnDepthLevel?: number[];
  manualFiltering?: boolean;
  footer?: React.ReactNode | false;
}>;

export const BookingsTableInstanceProvider = ({
  children,
  columns,
  tableName,
  tableData,
  initialColumnFilters: _initialColumnFilters,
  initialColumnVisibility,
  initialSorting,
  clickableColumns,
  keepExpandedRowsOnDepthLevel,
  footer = true,
}: Props) => {
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const [refreshingBookingText, setRefreshingBookingText] = useState(false);

  const { globalFilter, onGlobalFilterChange } = useGlobalFilters(tableName);

  const initialColumnFilters = useMemo(() => {
    return _initialColumnFilters;
  }, [_initialColumnFilters]);

  const { columnFilters, onColumnFilterChange } = useTableFilters(
    tableName,
    initialColumnFilters,
    true,
  );

  return (
    <TableInstanceProvider
      tableName={tableName}
      expanded={expanded}
      getExpandedRowModel={getExpandedRowModel}
      getSubRows={(row) => row.subRows}
      onExpandedChange={setExpanded}
      keepExpandedRowsOnDepthLevel={keepExpandedRowsOnDepthLevel}
      filterFromLeafRows={false}
      data={tableData ?? []}
      columns={columns}
      clickableColumns={clickableColumns}
      initialColumnVisibility={initialColumnVisibility}
      meta={{
        refreshingBookingText,
        setRefreshingBookingText,
      }}
      initialSorting={initialSorting}
      columnFilters={columnFilters}
      globalFilter={globalFilter}
      onGlobalFilterChange={onGlobalFilterChange}
      onColumnFilterChange={onColumnFilterChange}
      footer={footer}
    >
      {children}
    </TableInstanceProvider>
  );
};
